/**
 * The passage language rule — plan-018 DOM, design §30.6.8 (ED-18). Pure; IDX-A applies it to every source section.
 *
 * ```
 * language(section) =
 *   1. the page's <html lang> or the document's declared language, when its primary subtag is one of the version's
 *      configured languages
 *   2. else a script guess over the section's letters among the configured languages: a script holding at least
 *      SCRIPT_SHARE (60 %) of the letters decides — Greek → el, Hebrew → he, Arabic → ar (each only when configured);
 *      Cyrillic → the configured Cyrillic-script language when exactly one; Latin → the configured Latin-script language
 *      when exactly one, and when several, the stop-word share guess below
 *   3. else the version's opening language (the first configured language)
 * ```
 *
 * THE STOP-WORD SHARE GUESS (plan-026 §1.6, design §37.4.6; amended 2026-10-21). With en and es, or es and pt, configured
 * a Latin-script passage used to take the opening language — the wrong stemmer, the wrong translation source, the wrong
 * card language. Now, when the script is Latin and more than one configured language is written in it, each candidate
 * scores `share = |tokens ∈ stopWordsOf(language)| / |tokens|` over the passage's word tokens (folded `strip_marks`, the
 * fold the packs' `search.stop_words` are stored in); the highest share wins when it is at least
 * {@link PASSAGE_LANGUAGE_STOP_SHARE_MINIMUM} and at least {@link PASSAGE_LANGUAGE_STOP_SHARE_LEAD} times the second,
 * otherwise rule 3 holds. `stopWordsOf` is pure and supplied by the caller (the factory reads the installed packs); a
 * language it answers `null` for scores 0. A declared language still wins first, one Latin-script language still needs
 * no guess, and Greek, Hebrew, Arabic and Cyrillic are unchanged — so an el + en version reads exactly as before.
 *
 * The rule never invents a language: every answer is one of the configured languages, so a passage is always searched
 * and rendered in a language the version speaks.
 */
import { ValidationError } from "../error/taxonomy.js";
import { foldForSearch } from "./fold.js";
import { primarySubtagOf } from "./text-search-config.js";
/** The share of a section's letters one script must hold to decide its language. */
export const SCRIPT_SHARE = 0.6;
/**
 * The stop-word share the leading Latin-script candidate must reach (design §37.4.6). Provisional: a first value, to be
 * measured on the es/pt evaluation set.
 */
export const PASSAGE_LANGUAGE_STOP_SHARE_MINIMUM = 0.05;
/**
 * How many times the second candidate's share the leading one must reach (design §37.4.6). Provisional: a first value,
 * to be measured on the es/pt evaluation set.
 */
export const PASSAGE_LANGUAGE_STOP_SHARE_LEAD = 1.5;
const NO_STOP_WORD = () => null;
const NOT_LETTER_OR_DIGIT = /[^\p{L}\p{N}]+/u;
const LETTER = /\p{L}/u;
const SCRIPT_PATTERN = [
    ["greek", /\p{Script=Greek}/u],
    ["hebrew", /\p{Script=Hebrew}/u],
    ["arabic", /\p{Script=Arabic}/u],
    ["cyrillic", /\p{Script=Cyrillic}/u],
    ["latin", /\p{Script=Latin}/u],
];
/** The primary subtags whose language is written in each single-language script. */
const SINGLE_LANGUAGE_SCRIPT = { greek: "el", hebrew: "he", arabic: "ar" };
/** Primary subtags of languages written in Cyrillic (the launch set and its neighbours). */
const CYRILLIC_LANGUAGE = new Set(["ru", "uk", "bg", "sr", "mk", "be", "kk"]);
/** Primary subtags of languages written in a script other than Latin, so "the configured Latin-script language" excludes them. */
const NON_LATIN_LANGUAGE = new Set(["el", "he", "ar", "fa", "ur", ...CYRILLIC_LANGUAGE, "zh", "ja", "ko", "hi", "th"]);
function scriptOf(text) {
    const count = new Map();
    let letter = 0;
    for (const character of text) {
        if (!/\p{L}/u.test(character))
            continue;
        letter += 1;
        const match = SCRIPT_PATTERN.find(([, pattern]) => pattern.test(character));
        if (match !== undefined)
            count.set(match[0], (count.get(match[0]) ?? 0) + 1);
    }
    if (letter === 0)
        return null;
    for (const [script, n] of count) {
        if (n / letter >= SCRIPT_SHARE)
            return script;
    }
    return null;
}
/** The passage's word tokens (a run of letters and digits holding a letter), folded `strip_marks`. */
function wordTokensOf(text) {
    return foldForSearch(text, "strip_marks")
        .split(NOT_LETTER_OR_DIGIT)
        .filter((one) => LETTER.test(one));
}
/**
 * The candidate whose stop words take the clearly largest share of the passage's word tokens, or `undefined` when none
 * reaches {@link PASSAGE_LANGUAGE_STOP_SHARE_MINIMUM} or the lead over the second is under
 * {@link PASSAGE_LANGUAGE_STOP_SHARE_LEAD} (a tie included).
 */
function stopWordGuessOf(candidate, stopWordsOf, text) {
    const token = wordTokensOf(text);
    if (token.length === 0)
        return undefined;
    const scored = candidate
        .map((language) => {
        const stopWord = stopWordsOf(language);
        const hit = stopWord === null ? 0 : token.filter((one) => stopWord.has(one)).length;
        return { language, share: hit / token.length };
    })
        .sort((one, other) => other.share - one.share);
    const [first, second] = scored;
    if (first === undefined || first.share < PASSAGE_LANGUAGE_STOP_SHARE_MINIMUM)
        return undefined;
    if (second !== undefined && first.share < PASSAGE_LANGUAGE_STOP_SHARE_LEAD * second.share)
        return undefined;
    return first.language;
}
/**
 * Builds the rule. Throws `ValidationError` when no language is configured (a version always has one).
 *
 * `stopWordsOf` gives each configured language's folded search stop words for the Latin-script guess (design §37.4.6).
 * Left out, no language has any, and a Latin-script passage with several Latin-script languages configured takes the
 * opening language, as before plan-026 — the build always passes the installed packs' words.
 */
export function passageLanguageRuleOf(configured, stopWordsOf = NO_STOP_WORD) {
    const [opening] = configured;
    if (opening === undefined) {
        throw new ValidationError({ code: "validation_failed", message: "A version has at least one language.", field: "language_code" });
    }
    const byPrimary = (primary) => configured.find((one) => primarySubtagOf(one) === primary);
    const onlyOf = (accept) => {
        const matching = configured.filter((one) => accept(primarySubtagOf(one)));
        return matching.length === 1 ? matching[0] : undefined;
    };
    const latin = configured.filter((one) => !NON_LATIN_LANGUAGE.has(primarySubtagOf(one)));
    return Object.freeze({
        configured: Object.freeze([...configured]),
        languageOf(declared, text) {
            if (declared !== null) {
                const stated = byPrimary(primarySubtagOf(declared.trim()));
                if (stated !== undefined)
                    return stated;
            }
            const script = scriptOf(text);
            if (script !== null) {
                const single = SINGLE_LANGUAGE_SCRIPT[script];
                const guessed = single !== undefined
                    ? byPrimary(single)
                    : script === "cyrillic"
                        ? onlyOf((primary) => CYRILLIC_LANGUAGE.has(primary))
                        : latin.length > 1
                            ? stopWordGuessOf(latin, stopWordsOf, text)
                            : onlyOf((primary) => !NON_LATIN_LANGUAGE.has(primary));
                if (guessed !== undefined)
                    return guessed;
            }
            return opening;
        },
    });
}
//# sourceMappingURL=passage-language.js.map